import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';

function DashboardPage() {
  const { user, login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // ตรวจสอบ token จาก URL parameters (สำหรับ OAuth login)
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // บันทึก token และ user data
      localStorage.setItem('token', token);
      
      // Decode JWT token เพื่อดึงข้อมูล user
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userData = payload.user;
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Redirect กลับไปหน้า dashboard โดยไม่มี token ใน URL
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Error decoding token:', error);
        navigate('/login?error=invalid_token');
      }
    }
  }, [location, navigate]);

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
      textAlign: 'center',
      marginBottom: '40px',
      padding: '30px 0',
    },
    welcomeTitle: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1a202c',
      marginBottom: '8px',
      background: 'linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    welcomeSubtitle: {
      fontSize: '18px',
      color: '#718096',
      fontWeight: '400',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '40px',
    },
    statCard: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(129, 212, 250, 0.1)',
      border: '1px solid rgba(129, 212, 250, 0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    statIcon: {
      fontSize: '32px',
      marginBottom: '16px',
      display: 'block',
    },
    statTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#4a5568',
      marginBottom: '8px',
    },
    statValue: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1a202c',
      marginBottom: '4px',
    },
    statDescription: {
      fontSize: '14px',
      color: '#718096',
    },
    profileSection: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      borderRadius: '20px',
      padding: '32px',
      boxShadow: '0 4px 20px rgba(129, 212, 250, 0.1)',
      border: '1px solid rgba(129, 212, 250, 0.1)',
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1a202c',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    sectionIcon: {
      fontSize: '24px',
      color: '#81d4fa',
    },
    profileGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
    },
    profileItem: {
      padding: '16px',
      backgroundColor: 'rgba(129, 212, 250, 0.05)',
      borderRadius: '12px',
      border: '1px solid rgba(129, 212, 250, 0.1)',
    },
    profileLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#718096',
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    profileValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a202c',
    },
    quickActions: {
      marginTop: '40px',
    },
    actionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
    },
    actionButton: {
      background: 'linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      textDecoration: 'none',
    },
  };

  // Mock data for stats
  const stats = [];

  const quickActions = [
    {
      icon: '✈️',
      text: t('planTrip'),
      action: () => window.location.href = '/plan-trip'
    },
    {
      icon: '📊',
      text: t('log'),
      action: () => window.location.href = '/analytics'
    },
    {
      icon: '❓',
      text: t('help'),
      action: () => window.location.href = '/help'
    }
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.welcomeTitle}>
          {t('hello')}, {user?.username}! 👋
        </h1>
        <p style={styles.welcomeSubtitle}>
          {t('welcome')} - {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Stats Grid */}
      {/* <div style={styles.statsGrid}>
        {stats.map((stat, index) => (
          <div 
            key={index} 
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px)';
              e.target.style.boxShadow = '0 8px 30px rgba(129, 212, 250, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 20px rgba(129, 212, 250, 0.1)';
            }}
          >
            <span style={styles.statIcon}>{stat.icon}</span>
            <div style={styles.statTitle}>{stat.title}</div>
            <div style={styles.statValue}>{stat.value}</div>
            <div style={styles.statDescription}>{stat.description}</div>
          </div>
        ))}
      </div> */}

      {/* Profile Section */}
      <div style={styles.profileSection}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.sectionIcon}>👤</span>
          {t('profileData')}
        </h2>
        <div style={styles.profileGrid}>
          <div style={styles.profileItem}>
            <div style={styles.profileLabel}>{t('username')}</div>
            <div style={styles.profileValue}>{user?.username}</div>
          </div>
          <div style={styles.profileItem}>
            <div style={styles.profileLabel}>{t('email')}</div>
            <div style={styles.profileValue}>{user?.email}</div>
          </div>
          <div style={styles.profileItem}>
            <div style={styles.profileLabel}>{t('userId')}</div>
            <div style={styles.profileValue}>{user?.id}</div>
          </div>
          <div style={styles.profileItem}>
            <div style={styles.profileLabel}>{t('role')}</div>
            <div style={styles.profileValue}>{user?.role || 'user'}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActions}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.sectionIcon}>⚡</span>
          {t('quickActions')}
        </h2>
        <div style={styles.actionsGrid}>
          {quickActions.map((action, index) => (
            <button
              key={index}
              style={styles.actionButton}
              onClick={action.action}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(129, 212, 250, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span>{action.icon}</span>
              {action.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;